// Mortgage calculation utilities

import type { Country, MortgageType, LoanProgram, FHADetails, VADetails, USDADetails, CMHCDetails, PaymentFrequency } from '../types/mortgageTypes';

/**
 * Calculate monthly rate for USA mortgages (monthly compounding)
 */
export const calculateUSAMonthlyRate = (annualRate: number): number => {
  return annualRate / 100 / 12;
};

/**
 * Calculate monthly rate for Canadian fixed-rate mortgages (semi-annual compounding)
 * Formula: monthly_rate = (1 + annual_rate/2)^(1/6) - 1
 */
export const calculateCanadianMonthlyRate = (annualRate: number): number => {
  const semiAnnualRate = annualRate / 100 / 2;
  const monthlyRate = Math.pow(1 + semiAnnualRate, 1/6) - 1;
  return monthlyRate;
};

/**
 * Calculate monthly rate based on country and mortgage type
 */
export const calculateMonthlyRate = (
  annualRate: number,
  country: Country,
  mortgageType: MortgageType
): number => {
  // Canadian variable-rate mortgages use monthly compounding (exception to the rule)
  if (country === 'Canada' && mortgageType === 'variable') {
    return calculateUSAMonthlyRate(annualRate);
  }
  
  // Canadian fixed-rate mortgages use semi-annual compounding
  if (country === 'Canada' && mortgageType === 'fixed') {
    return calculateCanadianMonthlyRate(annualRate);
  }
  
  // USA mortgages always use monthly compounding
  return calculateUSAMonthlyRate(annualRate);
};

/**
 * Calculate monthly payment using standard mortgage formula
 */
export const calculateMonthlyPayment = (
  principal: number,
  monthlyRate: number,
  numberOfPayments: number
): number => {
  if (monthlyRate === 0) {
    return principal / numberOfPayments;
  }
  
  return principal * (monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) / 
         (Math.pow(1 + monthlyRate, numberOfPayments) - 1);
};

/**
 * Calculate bi-weekly payment (26 payments per year)
 * Standard: (monthly × 12) / 26
 */
export const calculateBiWeeklyPayment = (monthlyPayment: number): number => {
  return (monthlyPayment * 12) / 26;
};

/**
 * Calculate accelerated bi-weekly payment
 * Accelerated: monthly / 2 (results in 13 monthly payments per year)
 */
export const calculateAcceleratedBiWeeklyPayment = (monthlyPayment: number): number => {
  return monthlyPayment / 2;
};

/**
 * Calculate semi-monthly payment (24 payments per year, on the 1st and 15th).
 * Not accelerated - annual cash equals monthly * 12.
 */
export const calculateSemiMonthlyPayment = (monthlyPayment: number): number => {
  return monthlyPayment / 2;
};

/**
 * Calculate weekly payment (52 payments per year).
 * Standard: (monthly * 12) / 52 - annual cash equals monthly * 12.
 */
export const calculateWeeklyPayment = (monthlyPayment: number): number => {
  return (monthlyPayment * 12) / 52;
};

/**
 * Calculate accelerated weekly payment (52 payments per year).
 * Accelerated: monthly / 4 (results in ~13 monthly payments per year).
 */
export const calculateAcceleratedWeeklyPayment = (monthlyPayment: number): number => {
  return monthlyPayment / 4;
};

/**
 * Number of scheduled payments per year for a given mortgage payment frequency.
 */
export const getPaymentsPerYear = (frequency: PaymentFrequency): number => {
  switch (frequency) {
    case 'monthly':
      return 12;
    case 'semi-monthly':
      return 24;
    case 'bi-weekly':
    case 'accelerated-bi-weekly':
      return 26;
    case 'weekly':
    case 'accelerated-weekly':
      return 52;
    default:
      return 12;
  }
};

/**
 * Dispatch monthly payment into the scheduled payment amount for the given
 * payment frequency. Accelerated variants pay more total per year (13 monthly
 * equivalents) than standard variants (12 monthly equivalents).
 */
export const getPaymentForFrequency = (
  monthlyPayment: number,
  frequency: PaymentFrequency
): number => {
  switch (frequency) {
    case 'monthly':
      return monthlyPayment;
    case 'semi-monthly':
      return calculateSemiMonthlyPayment(monthlyPayment);
    case 'bi-weekly':
      return calculateBiWeeklyPayment(monthlyPayment);
    case 'accelerated-bi-weekly':
      return calculateAcceleratedBiWeeklyPayment(monthlyPayment);
    case 'weekly':
      return calculateWeeklyPayment(monthlyPayment);
    case 'accelerated-weekly':
      return calculateAcceleratedWeeklyPayment(monthlyPayment);
    default:
      return monthlyPayment;
  }
};

/**
 * True for any frequency that pays more often than once per calendar month.
 * The amortization engine aggregates these into one schedule row per month.
 */
export const isSubMonthlyFrequency = (frequency: PaymentFrequency): boolean => {
  return frequency !== 'monthly';
};

/**
 * Calculate FHA Upfront MIP (1.75% of base loan amount)
 */
export const calculateFHAUpfrontMIP = (loanAmount: number): number => {
  return loanAmount * 0.0175;
};

/**
 * Calculate FHA Annual MIP rate based on LTV and loan term
 */
export const calculateFHAAnnualMIPRate = (
  loanAmount: number,
  homePrice: number,
  amortizationYears: number
): number => {
  const ltv = (loanAmount / homePrice) * 100;
  
  // Simplified rates - actual rates vary by base loan amount too
  if (amortizationYears <= 15) {
    if (ltv <= 90) {
      return 0.0015; // 0.15%
    } else {
      return 0.004; // 0.40%
    }
  } else {
    // > 15 years
    if (ltv <= 95) {
      return 0.005; // 0.50%
    } else {
      return 0.0055; // 0.55%
    }
  }
};

/**
 * Calculate FHA MIP duration in years
 */
export const calculateFHAMIPDuration = (
  loanAmount: number,
  homePrice: number,
  amortizationYears: number
): number => {
  const ltv = (loanAmount / homePrice) * 100;
  
  if (amortizationYears <= 15 && ltv <= 90) {
    return 0; // No annual MIP
  } else if (ltv <= 90) {
    return 11; // 11 years
  } else {
    return amortizationYears; // Lifetime of loan
  }
};

/**
 * Create FHA details object
 */
export const createFHADetails = (
  loanAmount: number,
  homePrice: number,
  amortizationYears: number
): FHADetails => {
  return {
    upfrontMIP: calculateFHAUpfrontMIP(loanAmount),
    annualMIPRate: calculateFHAAnnualMIPRate(loanAmount, homePrice, amortizationYears),
    mipDurationYears: calculateFHAMIPDuration(loanAmount, homePrice, amortizationYears)
  };
};

/**
 * Calculate VA Funding Fee rate based on down payment and use
 */
export const calculateVAFundingFeeRate = (
  downPaymentPercentage: number,
  isFirstTimeUse: boolean
): number => {
  if (downPaymentPercentage >= 10) {
    return isFirstTimeUse ? 0.014 : 0.014; // 1.4% for both
  } else if (downPaymentPercentage >= 5) {
    return isFirstTimeUse ? 0.0165 : 0.0165; // 1.65% for both
  } else {
    return isFirstTimeUse ? 0.0218 : 0.0343; // 2.18% first time, 3.43% subsequent
  }
};

/**
 * Create VA details object
 */
export const createVADetails = (
  downPaymentPercentage: number,
  isFirstTimeUse: boolean = true,
  isDisabledVeteran: boolean = false
): VADetails => {
  return {
    fundingFeeRate: isDisabledVeteran ? 0 : calculateVAFundingFeeRate(downPaymentPercentage, isFirstTimeUse),
    isFirstTimeUse,
    isDisabledVeteran
  };
};

/**
 * Create USDA details object
 */
export const createUSDADetails = (): USDADetails => {
  return {
    upfrontFee: 0.01,    // 1% of loan amount
    annualFee: 0.0035    // 0.35% of loan balance
  };
};

/**
 * Calculate CMHC premium rate based on down payment percentage
 */
export const calculateCMHCPremiumRate = (downPaymentPercentage: number): number => {
  if (downPaymentPercentage >= 20) {
    return 0.024; // 2.40% (rarely used, for lender-insured)
  } else if (downPaymentPercentage >= 15) {
    return 0.028; // 2.80%
  } else if (downPaymentPercentage >= 10) {
    return 0.031; // 3.10%
  } else {
    return 0.04;  // 4.00%
  }
};

/**
 * Calculate minimum down payment for Canada (stepped)
 */
export const calculateCanadianMinimumDownPayment = (homePrice: number): number => {
  if (homePrice <= 500000) {
    return homePrice * 0.05; // 5% on first $500K
  } else if (homePrice <= 1000000) {
    return (500000 * 0.05) + ((homePrice - 500000) * 0.10); // 5% on first $500K, 10% on next $500K
  } else if (homePrice <= 1500000) {
    return (500000 * 0.05) + (500000 * 0.10) + ((homePrice - 1000000) * 0.20); // Stepped
  } else {
    return homePrice * 0.20; // 20% for > $1.5M
  }
};

/**
 * Calculate provincial sales tax on CMHC premium
 */
export const calculateCMHCProvincialTax = (
  premiumAmount: number,
  province: 'BC' | 'ON' | 'QC' | 'Other'
): number => {
  switch (province) {
    case 'ON':
      return premiumAmount * 0.08; // 8% PST
    case 'QC':
      return premiumAmount * 0.09; // 9% QST
    case 'BC':
      return premiumAmount * 0.07; // 7% PST
    default:
      return 0;
  }
};

/**
 * Create CMHC details object
 */
export const createCMHCDetails = (
  downPaymentPercentage: number,
  province: 'BC' | 'ON' | 'QC' | 'Other' = 'Other',
  includeProvincialTax: boolean = false
): CMHCDetails => {
  return {
    premiumRate: calculateCMHCPremiumRate(downPaymentPercentage),
    province,
    includeProvincialTax
  };
};

/**
 * Calculate total mortgage insurance (monthly) based on loan program
 */
export const calculateMortgageInsurance = (
  loanProgram: LoanProgram,
  loanAmount: number,
  homePrice: number,
  amortizationYears: number,
  paymentNumber: number,
  fhaDetails?: FHADetails,
  usdaDetails?: USDADetails
): number => {
  let monthlyInsurance = 0;
  
  if (loanProgram === 'fha' && fhaDetails) {
    // FHA annual MIP (if within duration)
    const yearNumber = Math.ceil(paymentNumber / 12);
    if (yearNumber <= fhaDetails.mipDurationYears) {
      monthlyInsurance = (loanAmount * fhaDetails.annualMIPRate) / 12;
    }
  } else if (loanProgram === 'usda' && usdaDetails) {
    // USDA annual fee
    monthlyInsurance = (loanAmount * usdaDetails.annualFee) / 12;
  }
  // Note: CMHC premium is added to loan amount, not paid monthly
  // VA has no monthly insurance, only upfront funding fee
  
  return monthlyInsurance;
};

/**
 * Check if down payment meets program requirements
 */
export const validateDownPayment = (
  loanProgram: LoanProgram,
  downPaymentPercentage: number,
  homePrice: number,
  country: Country
): { valid: boolean; message?: string; minimumRequired?: number } => {
  switch (loanProgram) {
    case 'fha':
      if (downPaymentPercentage < 3.5) {
        return { 
          valid: false, 
          message: 'FHA loans require a minimum 3.5% down payment',
          minimumRequired: 3.5
        };
      }
      break;
    
    case 'va':
    case 'usda':
      // No minimum down payment required
      break;
    
    case 'jumbo':
      if (downPaymentPercentage < 10) {
        return {
          valid: false,
          message: 'Jumbo loans typically require a minimum 10-20% down payment',
          minimumRequired: 10
        };
      }
      break;
    
    case 'cmhc':
      if (country === 'Canada') {
        const minimumDown = calculateCanadianMinimumDownPayment(homePrice);
        const minimumPercentage = (minimumDown / homePrice) * 100;
        if (downPaymentPercentage < minimumPercentage) {
          return {
            valid: false,
            message: `Canadian insured mortgages require ${minimumPercentage.toFixed(2)}% minimum down payment for this price`,
            minimumRequired: minimumPercentage
          };
        }
      }
      break;
    
    case 'conventional':
      // No hard minimum, but PMI required if < 20% in USA
      break;
  }
  
  return { valid: true };
};

/**
 * Calculate monthly PMI for a conventional loan.
 * PMI applies only when the loan program is conventional, the pmi value is
 * positive, and the remaining balance exceeds 80% of the original home price
 * (standard automatic cancellation threshold).
 */
export function calculateConventionalPMI(
  remainingBalance: number,
  homePrice: number,
  pmi: { type: 'percent' | 'amount'; value: number } | undefined,
  loanProgram: LoanProgram
): number {
  if (loanProgram !== 'conventional') return 0;
  if (!pmi || pmi.value <= 0) return 0;
  if (remainingBalance <= homePrice * 0.80) return 0;

  if (pmi.type === 'percent') {
    return (remainingBalance * (pmi.value / 100)) / 12;
  }
  return pmi.value;
}

/**
 * Get loan limit for program (USA)
 */
export const getLoanLimit = (loanProgram: LoanProgram, isHighCostArea: boolean = false): number | null => {
  switch (loanProgram) {
    case 'conventional':
      return isHighCostArea ? 1209750 : 806500; // 2026 conforming limits
    case 'fha':
      return isHighCostArea ? 1209750 : 524225; // 2026 FHA limits
    case 'va':
      return null; // No limit
    case 'usda':
      return null; // No hard limit, but income-based
    case 'jumbo':
      return null; // Above conforming limits
    default:
      return null;
  }
};
